
// All Whisper inference runs inside a Web Worker so the main thread never blocks.
// The main thread only handles audio decoding (fast, async) then transfers the
// Float32Array to the worker via zero-copy transferable.
//
// For files >= 3 min, audio is split into ~5-min chunks processed with 2 workers
// in parallel, giving a ~2–3× speedup for long recordings.

// ── Local Settings ────────────────────────────────────────────────────────────

const SETTINGS_KEY = 'echo_scribe_local_settings';

export interface LocalSettings {
  modelId: string;
  language: string;
  task: 'transcribe' | 'translate';
  beamSize: number;
  keepOriginal: boolean;
}

const DEFAULT_SETTINGS: LocalSettings = {
  modelId: 'onnx-community/whisper-base',
  language: 'auto',
  task: 'transcribe',
  beamSize: 5,
  keepOriginal: false,
};

export function getLocalSettings(): LocalSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw) as Partial<LocalSettings>;
    return { ...DEFAULT_SETTINGS, ...parsed };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveLocalSettings(settings: LocalSettings): void {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

// ─────────────────────────────────────────────────────────────────────────────

let worker: Worker | null = null;
let chunkWorkerPool: Worker[] | null = null;
let modelLoaded = false;

// ── Chunk processing constants ────────────────────────────────────────────────
const SAMPLE_RATE_HZ         = 16000;
const SHORT_FILE_THRESHOLD_S = 3 * 60;  // files < 3 min → single-worker fast path
const CHUNK_TARGET_S         = 5 * 60;  // target chunk length (5 min)
const OVERLAP_S              = 15;       // 15s overlap to avoid word cutoffs at boundaries
const MAX_PARALLEL_WORKERS   = 2;
// ─────────────────────────────────────────────────────────────────────────────

export const isModelLoaded = (): boolean => modelLoaded;

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(
      new URL('../workers/transcription.worker.ts', import.meta.url),
      { type: 'module' }
    );
  }
  return worker;
}

// Persistent pool for multi-chunk processing — workers are created once per session
// so the Whisper model loads at most MAX_PARALLEL_WORKERS times (not once per batch).
function getChunkWorkerPool(): Worker[] {
  if (!chunkWorkerPool) {
    chunkWorkerPool = Array.from({ length: MAX_PARALLEL_WORKERS }, () =>
      new Worker(
        new URL('../workers/transcription.worker.ts', import.meta.url),
        { type: 'module' }
      )
    );
  }
  return chunkWorkerPool;
}

// Decode audio file to mono Float32Array at 16kHz on the main thread (fast, async I/O).
async function fileToFloat32Array(file: File): Promise<Float32Array> {
  const arrayBuffer = await file.arrayBuffer();
  const audioContext = new AudioContext({ sampleRate: SAMPLE_RATE_HZ });
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
  await audioContext.close();

  const numChannels = audioBuffer.numberOfChannels;
  const length = audioBuffer.length;
  const mono = new Float32Array(length);

  for (let c = 0; c < numChannels; c++) {
    const channel = audioBuffer.getChannelData(c);
    for (let i = 0; i < length; i++) {
      mono[i] += channel[i] / numChannels;
    }
  }

  return mono;
}

// ── Audio splitting ───────────────────────────────────────────────────────────

interface AudioChunk {
  slice: Float32Array;
  offsetSec: number;
  index: number;
}

function splitAudio(mono: Float32Array): AudioChunk[] {
  const totalSec = mono.length / SAMPLE_RATE_HZ;

  if (totalSec < SHORT_FILE_THRESHOLD_S) {
    // Short file — return the original array directly (no copy)
    return [{ slice: mono, offsetSec: 0, index: 0 }];
  }

  const step = CHUNK_TARGET_S - OVERLAP_S; // 285s per step
  const chunks: AudioChunk[] = [];
  let chunkIndex = 0;

  while (true) {
    const startSec = chunkIndex * step;
    if (startSec >= totalSec) break;
    const endSec = Math.min(startSec + CHUNK_TARGET_S, totalSec);
    const startSample = Math.floor(startSec * SAMPLE_RATE_HZ);
    const endSample   = Math.floor(endSec   * SAMPLE_RATE_HZ);
    // .slice() produces a new ArrayBuffer — required for independent zero-copy transfer
    chunks.push({ slice: mono.slice(startSample, endSample), offsetSec: startSec, index: chunkIndex });
    chunkIndex++;
  }

  return chunks;
}

// ── Chunk result type ─────────────────────────────────────────────────────────

interface ChunkResult {
  index: number;
  offsetSec: number;
  lines: string[];
  transcript: string;
  error?: string;
}

// ── Timestamp parsing (for overlap deduplication) ─────────────────────────────

function parseTimestampSec(line: string): number {
  const match = line.match(/^\[(\d+):(\d+):(\d+)\]|^\[(\d+):(\d+)\]/);
  if (!match) return -1;
  if (match[1] !== undefined) {
    return parseInt(match[1]) * 3600 + parseInt(match[2]) * 60 + parseInt(match[3]);
  }
  return parseInt(match[4]) * 60 + parseInt(match[5]);
}

// ── Overlap deduplication ─────────────────────────────────────────────────────
// Chunk N covers audio [N*step, N*step + CHUNK_TARGET_S].
// Chunk N+1 starts at (N*step + step) = (N*step + CHUNK_TARGET_S - OVERLAP_S).
// The overlap region is [(N+1)*step, N*step + CHUNK_TARGET_S] = 15s of duplicated audio.
// Discard lines from nextLines with timestamp < prevOffsetSec + CHUNK_TARGET_S.

function deduplicateOverlap(prevOffsetSec: number, nextLines: string[]): string[] {
  const cutoffSec = prevOffsetSec + CHUNK_TARGET_S;
  return nextLines.filter(line => {
    const sec = parseTimestampSec(line);
    return sec < 0 || sec >= cutoffSec; // keep if unparseable or past the overlap boundary
  });
}

// ── Transcribe one chunk with a dedicated worker ──────────────────────────────

function transcribeChunk(w: Worker, chunk: AudioChunk): Promise<ChunkResult> {
  return new Promise<ChunkResult>((resolve) => {
    const lines: string[] = [];

    const handler = (event: MessageEvent) => {
      const msg = event.data;
      switch (msg.type) {
        case 'chunk':
          lines.push(msg.text as string);
          break;
        case 'done':
          modelLoaded = true;
          w.removeEventListener('message', handler);
          resolve({ index: chunk.index, offsetSec: chunk.offsetSec, lines, transcript: lines.join('\n') });
          break;
        case 'error':
          w.removeEventListener('message', handler);
          resolve({ index: chunk.index, offsetSec: chunk.offsetSec, lines: [], transcript: '', error: msg.message as string });
          break;
      }
    };

    w.addEventListener('message', handler);
    // Transfer the slice's ArrayBuffer zero-copy — chunk.slice is neutered after this
    const settings = getLocalSettings();
    w.postMessage(
      { type: 'transcribe', audioData: chunk.slice.buffer, offsetSec: chunk.offsetSec, settings },
      [chunk.slice.buffer],
    );
  });
}

// ── Emit completed chunks in temporal order ───────────────────────────────────

function flushInOrder(
  results: (ChunkResult | undefined)[],
  nextToFlush: { value: number },
  onChunk: ((text: string) => void) | undefined,
  chunks: AudioChunk[],
): void {
  while (nextToFlush.value < results.length && results[nextToFlush.value] !== undefined) {
    const result = results[nextToFlush.value]!;

    if (result.error) {
      onChunk?.(`[Chunk ${result.index} error: ${result.error}]`);
    } else {
      const lines = result.index > 0
        ? deduplicateOverlap(chunks[result.index - 1].offsetSec, result.lines)
        : result.lines;
      lines.forEach(line => onChunk?.(line));
    }

    nextToFlush.value++;
  }
}

// ── Assemble final transcript from all chunk results ──────────────────────────

function assembleTranscript(results: (ChunkResult | undefined)[], chunks: AudioChunk[]): string {
  const allLines: string[] = [];

  for (let i = 0; i < results.length; i++) {
    const result = results[i];
    if (!result) continue;

    if (result.error) {
      allLines.push(`[ERROR in chunk ${result.index}: ${result.error}]`);
      continue;
    }

    const lines = i > 0
      ? deduplicateOverlap(chunks[i - 1].offsetSec, result.lines)
      : result.lines;

    allLines.push(...lines);
  }

  return allLines.join('\n');
}

// ── Main export ───────────────────────────────────────────────────────────────

export interface TranscribeResult {
  transcript: string;
  originalTranscript?: string;
}

export const transcribeMedia = async (
  file: File,
  onProgress?: (pct: number) => void,
  onChunk?: (text: string) => void,
): Promise<TranscribeResult> => {
  const settings = getLocalSettings();
  const mono = await fileToFloat32Array(file);
  const chunks = splitAudio(mono);

  // ── Helper: run a single-worker pass with given task override ────────────────
  const runSingleChunk = (slice: Float32Array, taskOverride?: 'transcribe' | 'translate'): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      const w = getWorker();
      const passSettings = taskOverride ? { ...settings, task: taskOverride } : settings;

      const handler = (event: MessageEvent) => {
        const msg = event.data;
        switch (msg.type) {
          case 'model-progress':
            onProgress?.(msg.pct as number);
            break;
          case 'chunk':
            onChunk?.(msg.text as string);
            break;
          case 'done':
            modelLoaded = true;
            w.removeEventListener('message', handler);
            resolve(msg.transcript as string);
            break;
          case 'error':
            w.removeEventListener('message', handler);
            reject(new Error(`Local transcription failed. ${msg.message}`));
            break;
        }
      };

      w.addEventListener('message', handler);
      w.postMessage({ type: 'transcribe', audioData: slice.buffer, settings: passSettings }, [slice.buffer]);
    });
  };

  // ── Fast path: single chunk (file < 3 min) ──────────────────────────────────
  if (chunks.length === 1) {
    const slice = chunks[0].slice;

    if (settings.task === 'translate' && settings.keepOriginal) {
      // Run sequentially — both passes use the same singleton worker,
      // running in parallel would cause both handlers to fire on the same 'done' event.
      const sliceCopy = slice.slice(0);
      const translated = await runSingleChunk(slice, 'translate');
      const original = await runSingleChunk(sliceCopy, 'transcribe');
      return { transcript: translated, originalTranscript: original };
    }

    const transcript = await runSingleChunk(slice);
    return { transcript };
  }

  // ── Multi-chunk parallel path ─────────────────────────────────────────────
  console.log(`[EchoScribe] Splitting ${file.name} into ${chunks.length} chunks (${MAX_PARALLEL_WORKERS} workers)`);

  const results: (ChunkResult | undefined)[] = new Array(chunks.length).fill(undefined);
  const nextToFlush = { value: 0 };

  const pool = getChunkWorkerPool();

  for (let batchStart = 0; batchStart < chunks.length; batchStart += MAX_PARALLEL_WORKERS) {
    const batch = chunks.slice(batchStart, batchStart + MAX_PARALLEL_WORKERS);
    console.log(`[EchoScribe] Processing chunks ${batch.map(c => c.index).join(', ')} in parallel`);

    const batchResults = await Promise.all(
      batch.map((chunk, i) => transcribeChunk(pool[i], chunk))
    );
    batchResults.forEach(r => { results[r.index] = r; });
    flushInOrder(results, nextToFlush, onChunk, chunks);
  }

  const transcript = assembleTranscript(results, chunks);

  // For multi-chunk translate + keepOriginal, run a second pass in transcribe mode
  if (settings.task === 'translate' && settings.keepOriginal) {
    const mono2 = await fileToFloat32Array(file);
    const chunks2 = splitAudio(mono2);
    const origResults: (ChunkResult | undefined)[] = new Array(chunks2.length).fill(undefined);
    const origNextToFlush = { value: 0 };

    for (let batchStart = 0; batchStart < chunks2.length; batchStart += MAX_PARALLEL_WORKERS) {
      const batch = chunks2.slice(batchStart, batchStart + MAX_PARALLEL_WORKERS);
      const batchResults = await Promise.all(
        batch.map((chunk, i) => {
          const w = pool[i];
          return new Promise<ChunkResult>((resolve) => {
            const lines: string[] = [];
            const handler = (event: MessageEvent) => {
              const msg = event.data;
              if (msg.type === 'chunk') lines.push(msg.text as string);
              else if (msg.type === 'done' || msg.type === 'error') {
                w.removeEventListener('message', handler);
                resolve({ index: chunk.index, offsetSec: chunk.offsetSec, lines, transcript: lines.join('\n'), error: msg.type === 'error' ? msg.message : undefined });
              }
            };
            w.addEventListener('message', handler);
            w.postMessage(
              { type: 'transcribe', audioData: chunk.slice.buffer, offsetSec: chunk.offsetSec, settings: { ...settings, task: 'transcribe' } },
              [chunk.slice.buffer],
            );
          });
        })
      );
      batchResults.forEach(r => { origResults[r.index] = r; });
      flushInOrder(origResults, origNextToFlush, undefined, chunks2);
    }

    return { transcript, originalTranscript: assembleTranscript(origResults, chunks2) };
  }

  return { transcript };
};
